import { useState, useEffect } from 'react';
import { checkApiHealth } from '../services/api';
import logger from '../utils/logger';
import { translations } from '../translations';

interface HealthCheckResult {
  isHealthy: boolean;
  isLoading: boolean;
  error: string | null;
  responseTime: number | null;
}

/**
 * API health check hook
 * Render uyku modu durumunu kontrol eder ve response time'ı ölçer
 */
export const useApiHealthCheck = (): HealthCheckResult => {
  const [isHealthy, setIsHealthy] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [responseTime, setResponseTime] = useState<number | null>(null);

  useEffect(() => {
    const checkHealth = async () => {
      setIsLoading(true);
      setError(null);
      const startTime = Date.now();

      try {
        // Use shorter timeout for health check (5 seconds instead of 60)
        // This prevents blocking app startup when backend is in sleep mode
        const healthPromise = checkApiHealth();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Health check timeout')), 5000)
        );

        const health = await Promise.race([healthPromise, timeoutPromise]) as any;
        const endTime = Date.now();
        const duration = endTime - startTime;

        setResponseTime(duration);
        setIsHealthy(health.status === 'OK');
        
        // Eğer response time 10 saniyeden uzunsa, backend uyku modundan uyanıyor
        if (duration > 10000) {
          logger.warn('Backend woke up from sleep mode', { duration });
        }
      } catch (err: unknown) {
        logger.warn('API health check failed or timed out (non-blocking)', err);
        // Don't block app startup - assume healthy and continue
        // Health check is informational, not critical for app functionality
        setIsHealthy(true); // Assume healthy to allow app to continue
        setResponseTime(null);
        setError(null); // Don't show error to user
      } finally {
        setIsLoading(false);
      }
    };

    checkHealth();
  }, []);

  return { isHealthy, isLoading, error, responseTime };
};

